/**
 * <variant-split-select> — one of the two cascading controls rendered by
 * snippets/variant-split-picker.liquid (Display brand → Display model).
 *
 * Frontend-only layer over the real (hidden) option control: picking a model,
 * or a brand-less value, applies the mapped value to the option's hidden
 * variant-select via onOptionSelect, so all existing variant-picker behavior
 * (resolution, section morph, price, variant-message, add-to-cart gating)
 * flows unchanged. Internal change/click events never bubble past this
 * element, so variant-picker only ever hears from the real control.
 */
class VariantSplitSelect extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#init();

    if (!VariantSplitSelect.documentBound) {
      VariantSplitSelect.documentBound = true;
      document.addEventListener('click', () => {
        document.querySelectorAll('variant-split-select .cs-menu.open').forEach((menu) => {
          menu.classList.remove('open');
          menu.closest('.custom-select')?.setAttribute('aria-expanded', 'false');
        });
      });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** Called by variant-picker after a section morph replaces our children. */
  afterSectionMorph() {
    this.#init();
  }

  get splitRole() {
    return this.dataset.splitRole;
  }

  get isButtons() {
    return this.dataset.splitStyle === 'buttons';
  }

  get picker() {
    return this.closest('variant-picker');
  }

  get optionWrapper() {
    return this.closest('.variant-option');
  }

  #init() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    if (this.isButtons) {
      // Radios bubble change; stop it here so variant-picker never treats a
      // split control as a real option control.
      this.addEventListener(
        'change',
        (event) => {
          event.stopPropagation();
          if (event.target instanceof HTMLInputElement) this.#handleEntry(event.target);
        },
        { signal }
      );
    } else {
      this.display = this.querySelector('.cs-display');
      this.menu = this.querySelector('.cs-menu');
      this.combobox = this.querySelector('.custom-select');

      this.combobox?.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          const entry = event.target instanceof Element ? event.target.closest('.cs-option') : null;
          if (entry) {
            if (entry.hidden || entry.classList.contains('opt-empty')) return;
            if (this.splitRole === 'accordion' && !entry.dataset.configValue) {
              // Brand row: expand/collapse its models, keep the menu open.
              this.#toggleGroup(entry);
              return;
            }
            this.#closeMenu();
            this.#handleEntry(entry);
            return;
          }
          this.#toggleMenu();
        },
        { signal }
      );

      this.combobox?.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.#toggleMenu();
          }
        },
        { signal }
      );
    }

    this.#applyInteractionState();
  }

  /**
   * Restore placeholder-vs-value state on load and after morphs. The hidden
   * real control's userHasInteracted (restored by variant-picker from select
   * names before this runs) is the source of truth; a brand picked without a
   * model yet survives morphs on the picker's splitState expando.
   */
  #applyInteractionState() {
    const state = this.#pickerState();
    const realInteracted = this.#realVariantSelect()?.userHasInteracted === true;

    if (this.splitRole === 'brand') {
      this.userHasInteracted = realInteracted || Boolean(state.brandKey) || Boolean(state.directLabel);
      if (!this.userHasInteracted) {
        this.#showPlaceholder();
      } else if (!realInteracted && state.brandLabel) {
        this.#setDisplayText(state.brandLabel);
      }
      return;
    }

    this.userHasInteracted = realInteracted;
    if (realInteracted) {
      if (this.splitRole === 'model' && state.directLabel) {
        // Brand-less selection: the model control sits disabled at its placeholder.
        this.userHasInteracted = false;
        this.#showPlaceholder();
        this.setDisabled(true);
      }
      return;
    }
    this.#showPlaceholder();
    if (this.splitRole === 'model') {
      if (state.brandKey) {
        this.setDisabled(false);
        this.filterByBrand(state.brandKey);
      } else {
        this.setDisabled(true);
      }
    }
  }

  /** Accordion brand row: expand this group, collapse the others. */
  #toggleGroup(row) {
    const expand = !row.classList.contains('expanded');
    this.querySelectorAll('.cs-option--group').forEach((group) => {
      const on = expand && group === row;
      group.classList.toggle('expanded', on);
      group.setAttribute('aria-expanded', String(on));
    });
    this.querySelectorAll('.cs-option--sub').forEach((sub) => {
      sub.hidden = !(expand && sub.dataset.brand === row.dataset.brandKey);
    });
  }

  /** @param {HTMLElement} el - the picked .cs-option or radio input */
  #handleEntry(el) {
    const state = this.#pickerState();
    this.userHasInteracted = true;
    this.#markSelected(el);
    this.#setDisplayText(this.#entryLabel(el));

    if (this.splitRole === 'brand') {
      const model = this.#siblingControl('model');
      if (el.dataset.configValue) {
        // Brand-less value: apply immediately; only this control is mandatory
        // and the second control stays disabled (Sean's rule).
        state.brandKey = null;
        state.brandLabel = this.#entryLabel(el);
        state.directLabel = this.#entryLabel(el);
        model?.reset({ disabled: true });
        this.#applyConfigValue(el.dataset.configValue);
      } else {
        state.brandKey = el.dataset.brandKey || null;
        state.brandLabel = this.#entryLabel(el);
        state.directLabel = null;
        model?.reset({ disabled: false });
        model?.filterByBrand(el.dataset.brandKey);
        // A fresh brand un-resolves the option until a model is picked, so
        // add-to-cart / variant-message wait again.
        this.#unresolveRealSelect();
      }
      return;
    }

    this.#applyConfigValue(el.dataset.configValue);
  }

  /** Apply a real option value through the hidden variant-select. */
  #applyConfigValue(value) {
    if (!value) return;
    const vs = this.#realVariantSelect();
    const select = vs?.querySelector('select');
    if (!vs || !select) return;

    const index = [...select.options].findIndex((option) => option.value === value);
    if (index < 0) return;

    vs.userHasInteracted = true;
    if (typeof vs.onOptionSelect === 'function' && vs.optionEls?.[index]) {
      vs.onOptionSelect(select.options[index], vs.optionEls[index]);
    } else {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }
    window.updateVariantMessageVisibility?.();
  }

  #unresolveRealSelect() {
    const vs = this.#realVariantSelect();
    if (vs) vs.userHasInteracted = false;
    window.checkAndApplyDisable?.();
    window.updateVariantMessageVisibility?.();
  }

  /** The hidden variant-select this split picker drives. */
  #realVariantSelect() {
    const name = `options[${this.dataset.targetOption}]`;
    const picker = this.picker;
    if (!picker) return null;
    return [...picker.querySelectorAll('variant-select')].find(
      (vs) => vs.querySelector('select')?.name === name
    );
  }

  /** @param {string} role */
  #siblingControl(role) {
    return this.picker?.querySelector(`variant-split-select[data-split-role="${role}"]`) ?? null;
  }

  #pickerState() {
    const picker = this.picker;
    if (!picker) return {};
    picker.splitState ||= {};
    return picker.splitState;
  }

  // ---- shared entry/list helpers -------------------------------------------

  #entryElements() {
    if (this.isButtons) return [...this.querySelectorAll('input[type="radio"]')];
    return [...this.querySelectorAll('.cs-option:not(.opt-empty)')];
  }

  /** @param {HTMLElement} el */
  #entryLabel(el) {
    if (el instanceof HTMLInputElement) return el.value;
    return el.textContent.trim();
  }

  /** @param {HTMLElement} el */
  #markSelected(el) {
    if (this.isButtons) {
      // The browser handles radio checked state.
      return;
    }
    this.#entryElements().forEach((entry) => {
      entry.classList.remove('selected');
      entry.setAttribute('aria-selected', 'false');
    });
    el.classList.add('selected');
    el.setAttribute('aria-selected', 'true');
    this.classList.add('has-value');
  }

  #setDisplayText(text) {
    if (this.display) this.display.textContent = text;
  }

  #showPlaceholder() {
    if (this.isButtons) {
      this.#entryElements().forEach((input) => {
        input.checked = false;
      });
      return;
    }
    const label = this.display?.dataset.customLabel;
    if (label) this.#setDisplayText(label);
    this.classList.remove('has-value');
    this.#entryElements().forEach((entry) => {
      entry.classList.remove('selected');
      entry.setAttribute('aria-selected', 'false');
    });
  }

  // ---- model-control API used by the brand control -------------------------

  /** @param {{ disabled: boolean }} options */
  reset({ disabled }) {
    this.userHasInteracted = false;
    this.#showPlaceholder();
    this.setDisabled(disabled);
  }

  /** @param {string | undefined} brandKey */
  filterByBrand(brandKey) {
    this.#entryElements().forEach((entry) => {
      const host = this.isButtons ? entry.closest('label') : entry;
      if (host) host.hidden = entry.dataset.brand !== brandKey;
    });
    const emptyRow = this.querySelector('.opt-empty');
    if (emptyRow) emptyRow.hidden = Boolean(brandKey);
  }

  /** @param {boolean} disabled */
  setDisabled(disabled) {
    this.optionWrapper?.classList.toggle('variant-option--split-disabled', disabled);
    if (this.isButtons) {
      this.#entryElements().forEach((input) => {
        input.disabled = disabled;
      });
    }
  }

  #toggleMenu() {
    if (!this.menu) return;
    const opening = !this.menu.classList.contains('open');
    document.querySelectorAll('variant-split-select .cs-menu.open, variant-select .cs-menu.open').forEach((menu) => {
      if (menu !== this.menu) {
        menu.classList.remove('open');
        menu.closest('.custom-select')?.setAttribute('aria-expanded', 'false');
      }
    });
    this.menu.classList.toggle('open', opening);
    this.combobox?.setAttribute('aria-expanded', String(opening));
  }

  #closeMenu() {
    this.menu?.classList.remove('open');
    this.combobox?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('variant-split-select')) {
  customElements.define('variant-split-select', VariantSplitSelect);
}
